import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Typography } from '@mui/material';
import { getAllFeatureFlags } from 'shared/constants/feature-flags';
import { useFeatureFlag } from 'shared/hooks/feature-flag';
import { Environment, ENVIRONMENTS, FeatureFlagConfig } from 'shared/models/feature-flag';
import { getCurrentEnvironment } from 'shared/utils/environment-utils';

import styles from './feature-flag-list.component.module.scss';

type FeatureFlagItemProps = {
  config: FeatureFlagConfig;
};

type EnvironmentRowProps = {
  environment: Environment;
  isCurrentEnv: boolean;
  label: string;
  value: string;
};

const EnvironmentRow = ({ environment, isCurrentEnv: isCurrentEnvironment, label, value }: Readonly<EnvironmentRowProps>): ReactElement => {
  const textColor = isCurrentEnvironment ? 'text.primary' : 'text.secondary';
  const fontWeight = isCurrentEnvironment ? 'bold' : 'normal';

  return (
    <div key={environment} className={styles.environmentRow}>
      <Typography className={styles.environmentLabel} variant="subtitle1" color={textColor} fontWeight={fontWeight}>
        {label}:
      </Typography>
      <Typography variant="subtitle1" color={textColor} fontWeight={fontWeight}>
        {value}
      </Typography>
    </div>
  );
};

const FeatureFlagItem = ({ config }: Readonly<FeatureFlagItemProps>): ReactElement => {
  const { t } = useTranslation('featureFlags');
  const { isEnabled } = useFeatureFlag(config.id);

  const currentEnvironment = getCurrentEnvironment();

  const environmentLabels: Record<Environment, string> = {
    development: t('environments.development'),
    test: t('environments.test'),
    acceptance: t('environments.acceptance'),
    production: t('environments.production'),
  };

  return (
    <li className={styles.featureFlagItem}>
      <div className={styles.featureFlagHeader}>
        {isEnabled ? <CheckCircleOutlineIcon color="success" /> : <BlockIcon color="disabled" />}
        <Typography variant="body1">
          {t(config.nameKey)}: {t(isEnabled ? 'status.enabled' : 'status.disabled')}
        </Typography>
      </div>
      <div className={styles.environmentList}>
        {ENVIRONMENTS.map((env) => {
          const enabledConfig = config.enabledIn.find((e) => e.environment === env);
          const value = enabledConfig ? t('fromVersion', { version: enabledConfig.fromVersion }) : t('disabled');

          return (
            <EnvironmentRow
              key={env}
              environment={env}
              isCurrentEnv={env === currentEnvironment}
              label={environmentLabels[env]}
              value={value}
            />
          );
        })}
      </div>
    </li>
  );
};

function FeatureFlagList(): ReactElement {
  const { t } = useTranslation('featureFlags');
  const featureFlags = getAllFeatureFlags();

  return (
    <div className={styles.featureFlagContainer}>
      <Typography variant="h5">{t('title')}</Typography>

      <ul className={styles.featureFlagList}>
        {featureFlags.map((config) => (
          <FeatureFlagItem key={config.id} config={config} />
        ))}
      </ul>
    </div>
  );
}

export default FeatureFlagList;
